import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, Save, Settings } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ownerAPI, OwnerAPIError } from '../api/owner';
import { ApplicationOwner, CreateOwnerRequest, UpdateOwnerRequest } from '../types/owner';
import { LicenseQuota } from '../components/LicenseQuota';

// バリデーションスキーマ
const ownerSettingsSchema = z.object({
  subsclight_api_key: z.string()
    .min(1, 'サブスクライトAPIキーは必須です')
    .min(10, 'APIキーは10文字以上で入力してください'),
  subsclight_site: z.string()
    .min(1, 'サイト識別子は必須です')
    .min(3, 'サイト識別子は3文字以上で入力してください'),
});

type OwnerSettingsForm = z.infer<typeof ownerSettingsSchema>;

export const OwnerSettingsPage: React.FC = () => {
  const [owner, setOwner] = useState<ApplicationOwner | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isNewOwner, setIsNewOwner] = useState(false);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm<OwnerSettingsForm>({
    resolver: zodResolver(ownerSettingsSchema),
    defaultValues: {
      subsclight_api_key: '',
      subsclight_site: '',
    },
  });

  useEffect(() => {
    loadOwnerData();
  }, []);

  const loadOwnerData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await ownerAPI.getMyOwner();
      console.log('Loaded owner data:', data);

      if (data.exists === false) {
        // オーナー情報が存在しない（初回設定）
        console.log('No existing owner found, setting up for new owner');
        setIsNewOwner(true);
        setOwner(null);
      } else {
        // 既存のオーナー情報
        console.log('Existing owner found:', data.owner_id);
        setOwner(data);
        setIsNewOwner(false);
        setValue('subsclight_site', data.subsclight_site);
        // APIキーは表示しない（マスキング済み）
      }
    } catch (err) {
      if (err instanceof OwnerAPIError) {
        setError(err.message);
      } else {
        setError('オーナー情報の読み込みに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: OwnerSettingsForm) => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      if (isNewOwner) {
        // 新規作成
        const request: CreateOwnerRequest = {
          subsclight_api_key: data.subsclight_api_key,
          subsclight_site: data.subsclight_site,
        };
        const newOwner = await ownerAPI.createOwner(request);
        setOwner(newOwner);
        setIsNewOwner(false);
        setSuccess('オーナー設定が正常に作成されました');
      } else {
        // 更新
        const request: UpdateOwnerRequest = {
          subsclight_api_key: data.subsclight_api_key,
          subsclight_site: data.subsclight_site,
        };
        console.log('Updating owner with:', request);
        const result = await ownerAPI.updateOwner(request);
        console.log('Update result:', result);
        setSuccess('オーナー設定が正常に更新されました');
        // 更新後にデータを再読み込み
        await loadOwnerData();
      }

      // フォームをクリア
      setValue('subsclight_api_key', '');
    } catch (err) {
      console.error('Owner settings error:', err);
      if (err instanceof OwnerAPIError) {
        setError(err.message);
      } else {
        setError('設定の保存に失敗しました');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={800} margin="auto" padding={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings />
        オーナー設定
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        サブスクライトAPIとの連携設定を管理します。この設定により、複数のアプリケーションを管理できるようになります。
      </Typography>

      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isNewOwner ? '初期設定' : '設定更新'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              {success}
            </Alert>
          )}

          {!isNewOwner && owner && (
            <Box sx={{ marginBottom: 3, padding: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                現在の設定
              </Typography>
              <Typography variant="body2">
                <strong>オーナーID:</strong> {owner.owner_id}
              </Typography>
              <Typography variant="body2">
                <strong>サイト識別子:</strong> {owner.subsclight_site}
              </Typography>
              <Typography variant="body2">
                <strong>APIキー:</strong> {owner.subsclight_api_key_masked || '***'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>更新日時:</strong> {new Date(owner.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="subsclight_site"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="サブスクライトサイト識別子"
                  fullWidth
                  margin="normal"
                  error={!!errors.subsclight_site}
                  helperText={errors.subsclight_site?.message || 'サブスクライトで設定されたサイト識別子を入力してください'}
                  required
                />
              )}
            />

            <Controller
              name="subsclight_api_key"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="サブスクライトAPIキー"
                  type={showApiKey ? 'text' : 'password'}
                  fullWidth
                  margin="normal"
                  error={!!errors.subsclight_api_key}
                  helperText={errors.subsclight_api_key?.message ||
                    (isNewOwner
                      ? 'サブスクライトで発行されたAPIキーを入力してください'
                      : '新しいAPIキーを入力すると現在の設定が更新されます'
                    )}
                  required={isNewOwner}
                  placeholder={isNewOwner ? '' : '変更する場合のみ入力'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowApiKey(!showApiKey)}
                          edge="end"
                        >
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Box sx={{ marginTop: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                disabled={submitting}
                sx={{ minWidth: 120 }}
              >
                {submitting ? '保存中...' : isNewOwner ? '作成' : '更新'}
              </Button>

              {!isNewOwner && (
                <Button
                  variant="outlined"
                  onClick={loadOwnerData}
                  disabled={submitting}
                >
                  リロード
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ライセンス枠表示セクション */}
      {!isNewOwner && owner && (
        <LicenseQuota
          owner={owner}
          onOwnerUpdate={loadOwnerData}
        />
      )}

      {/* 説明セクション */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            設定について
          </Typography>

          <Typography variant="body2" paragraph>
            <strong>サブスクライトサイト識別子：</strong>
            サブスクライトで設定されたあなたのサイトの識別子です。サブスクライトの管理画面で確認できます。
          </Typography>

          <Typography variant="body2" paragraph>
            <strong>サブスクライトAPIキー：</strong>
            サブスクライトで発行されたAPIキーです。このキーにより商品設定や販売情報を取得できます。
          </Typography>

          <Divider sx={{ margin: '16px 0' }} />

          <Typography variant="body2" color="text.secondary">
            <strong>セキュリティについて：</strong>
            APIキーは暗号化されて安全に保存されます。画面上ではマスキングされて表示されます。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};